/** @format */

import { useEffect, useState } from "react";
import {Order, OrderStatus as OrderStatusEnum, OrderStatus, UseOrders} from "../constants";
import {getAllDocs, getFilteredDocs, upsertDoc} from "../repositories/firebase";
import { COLLECTIONS } from "../constants/global";
import moment from "moment";
import { convertTimestamp } from "../modules";
import _ from "lodash";
import {User} from "@firebase/auth";
import {collection, documentId, getDocs, query, where} from "firebase/firestore";
import {db} from "../firebase";

export const useOrders = (): UseOrders => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const toMomentDate = (dateValue: any) => {
    if (!dateValue) return moment.invalid();
    if (dateValue instanceof Date) return moment(dateValue);
    if (typeof dateValue === "string" || typeof dateValue === "number") {
      return moment(dateValue);
    }
    if (typeof dateValue.toDate === "function") {
      return moment(dateValue.toDate());
    }
    if (typeof dateValue.seconds === "number") {
      return moment(convertTimestamp(dateValue));
    }
    return moment.invalid();
  };

  const isDateWithinLastThirtyDays = (dateValue: any, thirtyDaysAgo: Date) => {
    const date = toMomentDate(dateValue);
    return date.isValid() && date.isSameOrAfter(thirtyDaysAgo);
  };

  const getPatientsByIds = async (userIds: string[]) => {
    const userIdChunks = _.chunk(userIds.filter(Boolean), 30);
    const patientChunksResponses = await Promise.all(
      userIdChunks.map(async (chunk) => {
        const usersQuery = query(
          collection(db, COLLECTIONS.USERS),
          where(documentId(), "in", chunk)
        );
        const snapshot = await getDocs(usersQuery);
        return snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      })
    );

    return patientChunksResponses
      .flatMap((patients) => patients)
      .map((patient: any) => ({
        ...patient,
        dateOfBirth: convertTimestamp(patient.dateOfBirth as any),
      }));
  };

  const getOrders = async () => {
    try {
      const _orders = await getAllDocs<Order>(COLLECTIONS.ORDERS);
      let tempOrders = _orders.map((o) => {
        // Use linkLastSentAt (most recent send) if available, otherwise fallback to linkSentAt (first send), then createdAt
        // This ensures that resending a link gives the patient a fresh 7-day window
        const linkLastSentAt = o?.linkLastSentAt ? moment(convertTimestamp(o.linkLastSentAt as any)) : null;
        const linkSentAt = o?.linkSentAt ? moment(convertTimestamp(o.linkSentAt as any)) : null;
        const createdAt = moment(convertTimestamp(o?.createdAt as any));
        const currentStatusDate = moment(convertTimestamp(o?.currentStatus?.date as any));

        // Priority: linkLastSentAt > linkSentAt > max(currentStatus.date, createdAt)
        const expirationBaseTime = linkLastSentAt || linkSentAt || moment.max(createdAt, currentStatusDate);

        if (
          o.currentStatus.status === OrderStatus.WaitingResponse &&
          moment().diff(
              expirationBaseTime,
            "hours"
          ) >= 168 // 7 days = 168 hours
        ) {
          return {
            ...o,
            currentStatus: {
              ...o.currentStatus,
              status: OrderStatus.Refused,
            },
          };
        } else return o;
      });
      setOrders(tempOrders);

      return _orders;
    } catch (error) {
      console.log("Error getting orders!", error);
      return [];
    }
  };

  //** It will get orders from the last 30 days and filter them based on the status
  // and only gets patients for the orders which are going to begin to shown */
  const getLimitedOrders = async () => {
    try {
      const startTime = performance.now();


      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      
      const validStatuses = new Set([
        OrderStatusEnum.WaitingResponse,
        OrderStatusEnum.Ordered,
        OrderStatusEnum.Refused,
        OrderStatusEnum.KeepMeOnHold,
        OrderStatusEnum.WantsCall
      ]);

      const activeOrdersQuery = query(
        collection(db, COLLECTIONS.ORDERS),
        where("currentStatus.status", "in", Array.from(validStatuses))
      );
      const activeOrdersSnapshot = await getDocs(activeOrdersQuery);
      const ordersData = activeOrdersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Order[];

      console.log("Time taken to get orders", performance.now() - startTime);
      console.log("Main Orders length", ordersData.length);

      // Process and filter orders in a single pass
      const processedOrders = ordersData.reduce((acc, order) => {
        if (!validStatuses.has(order.currentStatus.status)) {
          return acc;
        }

        const shouldShowOrder =
          isDateWithinLastThirtyDays(order.createdAt, thirtyDaysAgo) ||
          isDateWithinLastThirtyDays(order.currentStatus?.date, thirtyDaysAgo) ||
          (
            order.currentStatus.status === OrderStatusEnum.Ordered &&
            isDateWithinLastThirtyDays(order.orderDate, thirtyDaysAgo)
          );

        if (!shouldShowOrder) {
          return acc;
        }

        let processedOrder = order;

        if (order.currentStatus.status === OrderStatus.WaitingResponse) {
          // Use linkLastSentAt (most recent send) if available, otherwise fallback to linkSentAt (first send), then createdAt
          // This ensures that resending a link gives the patient a fresh 7-day window
          const linkLastSentAt = order?.linkLastSentAt ? moment(convertTimestamp(order.linkLastSentAt as any)) : null;
          const linkSentAt = order?.linkSentAt ? moment(convertTimestamp(order.linkSentAt as any)) : null;
          const createdAt = moment(convertTimestamp(order?.createdAt as any));
          const currentStatusDate = moment(convertTimestamp(order?.currentStatus?.date as any));

          // Priority: linkLastSentAt > linkSentAt > max(currentStatus.date, createdAt)
          const expirationBaseTime = linkLastSentAt || linkSentAt || moment.max(createdAt, currentStatusDate);

          if (moment().diff(expirationBaseTime, "hours") >= 168) { // 7 days = 168 hours
            processedOrder = {
              ...order,
              currentStatus: {
                ...order.currentStatus,
                status: OrderStatus.Refused,
              },
            };
          }
        }

        acc.push(processedOrder);
        return acc;
      }, [] as Order[]);

      // Get unique user IDs using Set
      const userIds = _.uniq(processedOrders.map(o => o.userId));
      console.log("Users to process length", userIds.length);


      const patientStartTime = performance.now();

      const patients = await getPatientsByIds(userIds);
      const patientsMap = new Map(patients.map(patient => [patient.id, patient]));
      const ordersWithPatients = processedOrders.map(order => ({
        ...order,
        patient_details: patientsMap.get(order.userId)
      }));

      console.log("Time taken to get pts", performance.now() - patientStartTime);
      console.log("Orders length", ordersWithPatients.length);

      setOrders(ordersWithPatients);
      return ordersWithPatients;

    } catch (error) {
      console.log("Error getting orders!", error);
      return [];
    }
  }

  const getLimitedArchivedOrdersWithPagination = async (
      limit: number,
      page_no: number,
      userIds?: string[]
  ) => {
    try {
      const startTime = performance.now();

      // Build the filter conditions
      const conditions: any = {
        status: ['currentStatus.status', '==', OrderStatusEnum.Archived]
      };

      // Add user ID filter if provided
      if (userIds && userIds.length > 0) {
        conditions.userId = ['userId', 'in', userIds];
      }

      // Get archived orders with pagination, ordered by archived date (latest first)
      const { docs: ordersData, total: totalOrders } = await getFilteredDocs<Order>(
          COLLECTIONS.ORDERS,
          limit,
          page_no,
          conditions,
          {
            'currentStatus.date': 'desc'
          }
      );

      console.log("Time taken to get archived orders", performance.now() - startTime);
      console.log("Archived Orders length", ordersData.length);

      // Get unique user IDs from the filtered orders
      const orderUserIds = _.uniq(ordersData.map(o => o.userId));
      console.log("Users to process length", orderUserIds.length);

      const patientStartTime = performance.now();

      // Fetch patient details in chunks to avoid large queries
      const userIdChunks = _.chunk(orderUserIds, 30);
      const patientChunksResponses = await Promise.all(
          userIdChunks.map(chunk =>
              getFilteredDocs<User>(
                  COLLECTIONS.USERS,
                  null,
                  null,
                  {
                    id: ['id', 'in', chunk]
                  }
              )
          )
      );

      // Process patient data
      const patients = patientChunksResponses
          .flatMap(response => response.docs)
          .map((patient: any) => ({
            ...patient,
            dateOfBirth: convertTimestamp(patient.dateOfBirth as any),
          }));

      // Create a map for efficient patient lookup
      const patientsMap = new Map(patients.map(patient => [patient.id, patient]));

      // Combine orders with patient details
      const ordersWithPatients = ordersData.map(order => ({
        ...order,
        patient_details: patientsMap.get(order.userId)
      }));

      console.log("Time taken to get patients", performance.now() - patientStartTime);
      console.log("Orders with patient data", ordersWithPatients);

      return {
        orders: ordersWithPatients,
        total: totalOrders
      };

    } catch (error) {
      console.log("Error getting archived orders!", error);
      return {
        orders: [],
        total: 0
      };
    }
  }

  const saveOrder = async (
    _order: Order,
    options: { refetch: boolean } = { refetch: true }
  ): Promise<any> => {
    const { success, code, message } = await upsertDoc<Order>(
      COLLECTIONS.ORDERS,
      {
        ..._order,
      },
      _order?.id
    );
    if (success) {
      console.log("Order saved successfully!");
      if (options?.refetch) {
        //getOrders();
      }
    } else {
      console.log("Error saving Order!", message, code);
    }
  };

  useEffect(() => {
    (async () => {
      // setLoading(true);
      // await getOrders();
      //
      // setLoading(false);
    })();
  }, []);

  return {
    orders,
    loading,
    getOrders,
    saveOrder,
    getLimitedOrders,
    getLimitedArchivedOrdersWithPagination
  };
};
